import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { useWallet } from "../../../../hooks/useWallet";
import WalletTransactions from "./WalletTransactions";
import { useWalletTransactions } from "../../../../hooks/useWalletTransactions";
import { useWalletTransactionSearch } from "../../../../hooks/useWalletTransactionSearch";
import { InputSearch } from "../../../../components/InputSearch";

const WalletTransactionsSearch: React.FC = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { walletType, identityPubkey } = useWallet();
  const {
    transactions,
    pendingTransactions,
    isLoading,
    error,
    hasMore,
    loadTransactions,
    markAllTransactionsAsRead,
    nbTxUnread,
  } = useWalletTransactions();
  const {
    isSparkWallet,
    isSearchOpen,
    setIsSearchOpen,
    searchTerm,
    setSearchTerm,
    isSearchActive,
    searchedTransactions,
    isSearching,
    searchError,
    hasMoreSearchResults,
    loadMoreSearchResults,
  } = useWalletTransactionSearch({
    walletType,
    identityPubkey,
  });

  const transactionsWithPending = useMemo(() => [...pendingTransactions, ...transactions], [pendingTransactions, transactions]);

  const displayedTransactions = useMemo(
    () => (isSearchActive ? searchedTransactions : transactionsWithPending),
    [isSearchActive, searchedTransactions, transactionsWithPending]
  );

  const displayedLoading = isSearchActive ? isSearching : isLoading;
  const displayedError = isSearchActive ? undefined : error;
  const displayedHasMore = isSearchActive ? hasMoreSearchResults : hasMore;
  const onLoadMore = isSearchActive ? loadMoreSearchResults : loadTransactions;

  const { x, y, strategy, refs } = useFloating({
    placement: "bottom-end",
    middleware: [offset(8), flip(), shift()],
  });

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const handleSearchTransactions = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSearchOpen(true);
    setIsMenuOpen(false);
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    await markAllTransactionsAsRead();
  };

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (event.target instanceof Element && !event.target.closest(".wallet-transactions-menu")) {
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isMenuOpen, handleOutsideClick]);

  useEffect(() => {
    if (isSearchOpen) {
      // Wait for the input to be mounted before focusing.
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isSearchOpen]);

  return (
    <div className="container mx-auto">
      <div className="relative flex items-center justify-center">
        <div className="text-xl font-bold text-center">Transactions</div>
        {isSparkWallet && (
          <div className="absolute right-0 wallet-transactions-menu">
            <button
              ref={refs.setReference}
              type="button"
              onClick={handleMenuClick}
              className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-brand-bg-3"
              aria-label="Transactions menu"
            >
              <span className="material-symbols-outlined text-[20px]">more_vert</span>
            </button>

            {isMenuOpen && createPortal(
              <div
                ref={refs.setFloating}
                style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                }}
                className="wallet-transactions-menu w-56 bg-brand-bg-2 border border-brand-border-color rounded-md shadow shadow-grey dark:shadow dark:shadow-brand-grey z-50"
              >
                <button
                  type="button"
                  className={`flex items-center w-full px-4 py-2 hover:bg-brand-bg-3 cursor-pointer text-left ${nbTxUnread > 0 ? "border-b border-brand-border-color" : ""}`}
                  onClick={handleSearchTransactions}
                >
                  <span className="material-symbols-outlined text-[20px] mr-2">search</span>
                  <span className="text-sm">Search Transactions</span>
                </button>
                {nbTxUnread > 0 && (
                  <button
                    type="button"
                    className="flex items-center w-full px-4 py-2 hover:bg-brand-bg-3 cursor-pointer text-left"
                    onClick={handleMarkAllAsRead}
                  >
                    <span className="material-symbols-outlined text-[20px] mr-2">mark_email_read</span>
                    <span className="text-sm">Mark all as read</span>
                  </button>
                )}
              </div>,
              document.body
            )}
          </div>
        )}
      </div>

      {isSparkWallet && isSearchOpen && (
        <div className="mt-3">
          <InputSearch
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onClear={() => {
              setSearchTerm("");
              setIsSearchOpen(false);
            }}
            alwaysShowClear
            placeholder="Search transactions"
          />
        </div>
      )}

      <WalletTransactions
        transactions={displayedTransactions}
        isLoading={displayedLoading}
        error={displayedError}
        hasMore={displayedHasMore}
        onLoadMore={onLoadMore}
        showTitle={false}
        isSearchActive={isSearchActive}
        isSearching={isSearching}
        searchError={searchError}
      />
    </div>
  );
};

export default WalletTransactionsSearch;
